import styled from "@emotion/styled";
import type { StepColumnsType } from "@stepfinance/react";
import { ContextButton, StepTable } from "@stepfinance/react";
import type { TablePaginationConfig } from "antd/lib/table/interface";
import type { FC, SetStateAction } from "react";
import { useEffect, useState } from "react";

import { useDomainsContext } from "../../../contexts/domains";
import { useEnrichedWallet } from "../../../contexts/enrichedWallet";
import { useLanguageContext } from "../../../contexts/language/language";
import { isPaginationHidden, useLocalStorageState } from "../../../utils/utils";
import { ExplorerLink } from "../../ExplorerLink/ExplorerLink";
import { PAGE_SIZE_OPTIONS } from "../../shared";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";
import type { UserDomain } from "./Balances";

interface DomainsProps {
  setSendDomain: (value: SetStateAction<UserDomain | null>) => void;
}

export const Domains: FC<DomainsProps> = ({ setSendDomain }) => {
  const { userDomains } = useDomainsContext();
  const { watching } = useEnrichedWallet();
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "DomainsPaginationSizeSetting",
    PAGE_SIZE_OPTIONS.s[0],
  );
  const [pagination, setPagination] = useState<TablePaginationConfig>({});

  const {
    currentLanguageData: {
      common,
      pages: {
        dashboard: {
          modules: {
            balances: { domains: domainsText },
          },
        },
      },
    },
  } = useLanguageContext();

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(userDomains.length, PAGE_SIZE_OPTIONS.s),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.s,
      pageSize: parseInt(paginationSetting),
    });
  }, [userDomains.length, paginationSetting]);

  const domainColumns: StepColumnsType<UserDomain> = [
    {
      dataIndex: "domain",
      title: common.domain,
      key: "domain",
      render: (_, domain: UserDomain) => {
        const rootUrl = ".sol";
        return (
          <DomainCell>
            <DomainContainer>
              <ExplorerLink address={domain.nameKey.toString()} type="address">
                {domain.name}
                {rootUrl}
              </ExplorerLink>
            </DomainContainer>
            {!watching && <ContextButton onClick={() => setSendDomain(domain)}>{common.transfer}</ContextButton>}
          </DomainCell>
        );
      },
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
  ];

  return userDomains?.length ? (
    <DomainsContainer>
      <ChevronCollapse isCollapseChild title={domainsText} value={0}>
        <StepTable<UserDomain>
          columns={domainColumns}
          dataSource={userDomains}
          pagination={pagination}
          onChange={(pagination) => {
            setPagination(pagination);
            setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.s[0]);
          }}
          rowKey={(record) => record.nameKey.toBase58()}
        />
      </ChevronCollapse>
    </DomainsContainer>
  ) : (
    <></>
  );
};

const DomainsContainer = styled.div`
  margin-top: 20px;
  .ant-collapse-content-box {
    padding: 0 !important;
  }
`;

const DomainContainer = styled.div`
  min-width: 150px;
  display: flex;
  align-items: center;
  margin-right: 80px;
  font-weight: 700;
`;

const DomainCell = styled.div`
  display: flex;
  .send-transaction {
    margin-left: 16px;
  }
`;
